// Curated research stacks — pre-packaged groups of peptides sold together
// with a built-in discount. Each bundle references its members by product id.

export type Bundle = {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  productIds: string[]
  discount: number // 0–1, fraction off the bundle gross
  goals: Array<
    | 'Recovery'
    | 'Weight management'
    | 'Performance'
    | 'Skin & Hair'
    | 'Longevity'
    | 'Sleep'
  >
  badge?: 'Most popular' | 'New' | 'Editor pick'
  protocolLength: string // e.g. '6–8 weeks'
  primaryGoal:
    | 'Recovery'
    | 'Weight management'
    | 'Performance'
    | 'Skin & Hair'
    | 'Longevity'
    | 'Sleep'
  whyThisStack: string[] // 2–3 short reasons researchers pair these
}

export const BUNDLES: Bundle[] = [
  {
    id: 'recovery-stack',
    slug: 'recovery-stack',
    name: 'The Recovery Stack',
    tagline: 'BPC-157 + TB-500 — the soft-tissue research pairing.',
    description:
      "The two healing peptides researchers reach for first. BPC-157 drives the vascular response; TB-500 drives cellular migration. Stacked together they're the most-studied recovery pairing in peer-reviewed in-vitro literature.",
    productIds: ['bpc-157', 'tb-500'],
    discount: 0.12,
    goals: ['Recovery'],
    primaryGoal: 'Recovery',
    badge: 'Most popular',
    protocolLength: '6–8 weeks',
    whyThisStack: [
      'Two complementary mechanisms — vascular response + cellular migration',
      'Most-cited recovery pairing in published in-vitro work',
      'Shared protocol window — both ship reconstituted-ready',
    ],
  },
  {
    id: 'glp1-comparison',
    slug: 'glp1-research-pair',
    name: 'The GLP-1 Research Pair',
    tagline: 'Semaglutide + Tirzepatide for comparative protocols.',
    description:
      'For research designs comparing the two leading GLP-1 receptor agonists. Both shipped from the same dispatch with COA documentation matched.',
    productIds: ['semaglutide', 'tirzepatide'],
    discount: 0.1,
    goals: ['Weight management'],
    primaryGoal: 'Weight management',
    badge: 'Editor pick',
    protocolLength: '8–12 weeks',
    whyThisStack: [
      'Side-by-side comparative protocols on the two leading GLP-1 agonists',
      'Both batches from the same dispatch window — matched COAs',
      'Single cold-chain box, lower delta in handling variables',
    ],
  },
  {
    id: 'gh-axis-trio',
    slug: 'gh-axis-trio',
    name: 'The Growth Hormone Axis Trio',
    tagline: 'CJC-1295 + Ipamorelin + Tesamorelin.',
    description:
      'Three growth-hormone axis peptides covering the GHRH and GHS branches. The most common stack in published performance research.',
    productIds: ['cjc-1295', 'ipamorelin', 'tesamorelin'],
    discount: 0.15,
    goals: ['Performance'],
    primaryGoal: 'Performance',
    protocolLength: '12 weeks',
    whyThisStack: [
      'Covers both GHRH and GHS arms of the GH axis',
      'Largest discount in the catalogue — 15% off three vials',
      'Common in published performance-research stack protocols',
    ],
  },
  {
    id: 'longevity-stack',
    slug: 'longevity-stack',
    name: 'The Longevity Stack',
    tagline: 'Epitalon + NAD+ — telomere and mitochondrial research.',
    description:
      'Two peptides at the heart of aging research. Epitalon for telomerase activation studies, NAD+ for mitochondrial cofactor protocols.',
    productIds: ['epitalon', 'nad-plus'],
    discount: 0.1,
    goals: ['Longevity'],
    primaryGoal: 'Longevity',
    badge: 'New',
    protocolLength: '12 weeks',
    whyThisStack: [
      'Pairs telomere-axis and mitochondrial-axis research compounds',
      'Long-window protocol — bundled to support a full 12-week study',
      'Both compounds stable side-by-side in cold-chain storage',
    ],
  },
]

export function getBundleBySlug(slug: string): Bundle | undefined {
  return BUNDLES.find((b) => b.slug === slug)
}
